import Lenis from 'lenis';

/**
 * Singleton Lenis instance.
 *
 * Created lazily by getLenis() so React strict-mode double-mount or SSR
 * never instantiates more than one. Disabled when prefers-reduced-motion
 * is set or when window is unavailable (SSR/test).
 *
 * Sync with GSAP ScrollTrigger lives in `lib/gsap.ts` and runs once when
 * both modules are imported.
 */

let _lenis: Lenis | null = null;
let _rafHandle: number | null = null;

export function prefersReducedMotion(): boolean {
  if (typeof window === 'undefined' || !window.matchMedia) return false;
  return window.matchMedia('(prefers-reduced-motion: reduce)').matches;
}

export function getLenis(): Lenis | null {
  if (typeof window === 'undefined') return null;
  if (prefersReducedMotion()) return null;

  if (!_lenis) {
    _lenis = new Lenis({
      duration: 1.2,
      easing: (t) => Math.min(1, 1.001 - Math.pow(2, -10 * t)),
      orientation: 'vertical',
      gestureOrientation: 'vertical',
      smoothWheel: true,
      wheelMultiplier: 1,
      touchMultiplier: 2,
      // Native scroll on touch — better mobile perf, avoids gesture conflicts
      syncTouch: false,
    });
  }
  return _lenis;
}

/**
 * Standalone RAF loop. Only used if GSAP-driven ticker is NOT active
 * (e.g. early in app boot before lib/gsap.ts has imported). Once GSAP
 * ticker takes over, call stopStandaloneRaf() to avoid double-stepping.
 */
export function startStandaloneRaf(): void {
  if (_rafHandle !== null || !_lenis) return;
  const loop = (time: number) => {
    _lenis?.raf(time);
    _rafHandle = requestAnimationFrame(loop);
  };
  _rafHandle = requestAnimationFrame(loop);
}

export function stopStandaloneRaf(): void {
  if (_rafHandle !== null) {
    cancelAnimationFrame(_rafHandle);
    _rafHandle = null;
  }
}

export function destroyLenis(): void {
  stopStandaloneRaf();
  if (_lenis) {
    _lenis.destroy();
    _lenis = null;
  }
}

export function scrollTo(
  target: string | HTMLElement | number,
  options?: { offset?: number; duration?: number; immediate?: boolean }
): void {
  _lenis?.scrollTo(target, options);
}

export function stopLenis(): void {
  _lenis?.stop();
}

export function startLenis(): void {
  _lenis?.start();
}
